/**
 * §3.7 HALLUCINATION GATE — the shared, pure, verifiable grounding check.
 *
 * Every proper-noun place / outlet name in an LLM-generated text must appear in
 * the supplied evidence (signal titles, place names, origins) or the hazard
 * label, or be a common framing word. A name that does NOT is an un-sourced
 * entity → the text is WITHHELD. This is the council's cite-or-abstain guarantee
 * (docs/news-llm-engine-council-2026-06-15.md §3.7), enforced by code AFTER
 * generation — not by a prompt instruction the model can ignore.
 *
 * It was born inside the ESB generator (apps/ingestion bulletin.ts) and is now
 * lifted into @tsr/shared-types unchanged so BOTH the daemon-side ESB (#295) and
 * the on-demand Ask-the-Wire answer seam (#299, apps/api) validate against the
 * SAME gate — a single source of truth for a security-critical guardrail rather
 * than two copies that could drift. bulletin.ts re-exports `validateBriefGrounding`
 * so its existing import surface (and test) is unchanged.
 *
 * Pure: no DB, no LLM, no throw. Returns the un-sourced tokens (empty ⇒ grounded).
 */

/** Normalize a token for grounding comparison: lowercase + strip diacritics. */
export function normGroundingToken(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');
}

/** Common EN/FR framing/function words the model legitimately capitalizes (at a
 *  sentence start) that are NOT place or outlet names — so the gate never
 *  withholds valid text over them. Place names always come from the evidence. */
export const GROUNDING_SAFE_WORDS = new Set<string>(
  [
    // EN framing
    'the', 'a', 'an', 'this', 'that', 'these', 'those', 'there', 'here', 'and', 'or', 'but',
    'no', 'not', 'yet', 'officials', 'official', 'social', 'news', 'media', 'multiple',
    'several', 'reports', 'reported', 'reporting', 'coverage', 'area', 'region', 'near',
    'canada', 'canadian', 'summary', 'sources', 'source', 'posts', 'post', 'stories', 'story',
    'outlets', 'accounts', 'first', 'seen', 'ahead', 'feed', 'confirmed', 'corroboration',
    'authorities', 'residents', 'people', 'across', 'within', 'amid', 'while', 'after',
    'before', 'reportedly', 'trending', 'review', 'operator', 'users', 'posters',
    // Ask-the-Wire answer framing — measured, hedged phrasing the model opens
    // sentences with; place names still come exclusively from the evidence set.
    'nothing', 'wire', 'about', 'based', 'available', 'currently', 'appears', 'possible',
    'unconfirmed', 'signals', 'signal', 'according', 'context', 'note', 'question', 'answer',
    'however', 'unclear', 'limited', 'recent', 'latest', 'around', 'reportedly',
    // FR framing
    'le', 'la', 'les', 'un', 'une', 'des', 'du', 'de', 'non', 'confirme', 'officiel',
    'officielle', 'officielles', 'officiels', 'sociale', 'sociales', 'social', 'plusieurs',
    'region', 'regions', 'zone', 'pres', 'aucun', 'flux', 'examen', 'requis', 'article',
    'articles', 'publication', 'publications', 'nouvelles', 'medias', 'autorites',
    'residents', 'selon', 'plusieurs', 'apres', 'pendant', 'corroboration',
    'confirmation', 'incident', 'status', 'update', 'alert', 'warning', 'emergency',
    'developing', 'breaking', 'widespread', 'community', 'electricity', 'hydro',
    'rien', 'concernant', 'disponible', 'actuellement',
    // Month + weekday names (EN + FR) — text legitimately references a date.
    'january', 'february', 'march', 'april', 'may', 'june', 'july', 'august',
    'september', 'october', 'november', 'december', 'jan', 'feb', 'mar', 'apr',
    'jun', 'jul', 'aug', 'sep', 'sept', 'oct', 'nov', 'dec',
    'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday',
    'janvier', 'fevrier', 'mars', 'avril', 'mai', 'juin', 'juillet', 'aout',
    'septembre', 'octobre', 'novembre', 'decembre', 'lundi', 'mardi', 'mercredi',
    'jeudi', 'vendredi', 'samedi', 'dimanche',
  ].map(normGroundingToken),
);

/** Sentence-terminating punctuation — a capitalized word right after one of
 *  these (or at the very start) is grammatically capitalized, not a proper noun. */
const SENTENCE_BOUNDARY = new Set(['.', '!', '?', ':', ';', '\n', '\r', '•', '–', '—', '·']);

/** True when the token at `idx` opens a sentence (so its capital is grammatical). */
function isSentenceInitial(text: string, idx: number): boolean {
  let i = idx - 1;
  while (i >= 0 && /\s/.test(text[i]!)) i -= 1;
  if (i < 0) return true;
  return SENTENCE_BOUNDARY.has(text[i]!);
}

/** Significant proper-noun candidates: capitalized, ≥4 letters, not sentence-initial. */
export function properNounCandidates(text: string): string[] {
  const out: string[] = [];
  const re = /[\p{L}][\p{L}.'’-]*/gu;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    const raw = m[0].replace(/[.'’-]+$/g, '');
    if (raw.length < 4) continue;
    const first = raw[0]!;
    if (first !== first.toUpperCase() || first === first.toLowerCase()) continue; // not capitalized
    // Skip sentence-initial words: capitalization there is grammatical, not a
    // proper-noun signal — else "Confirmation", "Status", "Officials" at a
    // sentence start would false-withhold a perfectly grounded text. A
    // hallucinated place almost always appears mid-sentence in a locative phrase
    // ("near Sudbury"), which this still catches.
    if (isSentenceInitial(text, m.index)) continue;
    out.push(raw);
  }
  return out;
}

/**
 * THE §3.7 HALLUCINATION GATE. Every proper-noun place / outlet name in the text
 * must appear in the supplied evidence (titles, place names, origins) or the
 * hazard label, or be a common framing word. A name that does not is an
 * un-sourced entity → withheld. Returns the un-sourced tokens (empty ⇒ grounded).
 */
export function validateBriefGrounding(
  summary: string,
  evidence: ReadonlyArray<{ title: string | null; geoName: string | null; origin: string }>,
  hazardLabels: readonly string[],
): { ok: boolean; unsourced: string[] } {
  const allowed = new Set<string>(GROUNDING_SAFE_WORDS);
  const addWords = (s: string | null): void => {
    if (!s) return;
    for (const w of s.split(/[^\p{L}\p{N}]+/u)) {
      if (w) allowed.add(normGroundingToken(w));
    }
  };
  for (const e of evidence) {
    addWords(e.title);
    addWords(e.geoName);
    addWords(e.origin);
  }
  for (const label of hazardLabels) addWords(label);

  const unsourced: string[] = [];
  const seen = new Set<string>();
  for (const cand of properNounCandidates(summary)) {
    const norm = normGroundingToken(cand);
    if (allowed.has(norm) || seen.has(norm)) continue;
    seen.add(norm);
    unsourced.push(cand);
  }
  return { ok: unsourced.length === 0, unsourced };
}
